import { useMemo, useState } from 'react';
import { ArrowDownUp, ArrowUpDown, Download, MoreHorizontal, Plus, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import DataTable from '@/components/data-table/data-table';
import { formatNumber } from '@/utils/format';

import type { DispatchPlace, DispatchPlaceMetrics, DispatchPlaceStatus, DispatchPlaceType } from '@/features/dispatch-places/dispatch-places.types';
import type { ColumnDef } from '@tanstack/react-table';

import { DispatchPlaceDrawer } from './dispatch-place-drawer';
import { useDispatchPlacesPage } from './use-dispatch-places-page';
import type { SortField } from './use-dispatch-places-page';

const TYPE_LABEL: Record<DispatchPlaceType, string> = {
  physical: 'Físico',
  locker: 'Locker',
  virtual: 'Virtual',
};

const STATUS_LABEL: Record<DispatchPlaceStatus, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  pending: 'Pendiente',
};

const STATUS_BADGE_CLASS: Record<DispatchPlaceStatus, string> = {
  active: 'badge-success',
  inactive: 'badge-error',
  pending: 'badge-warning',
};

/**
 * The six order-status buckets shown per dispatch place, in display order.
 */
const METRIC_GROUPS: { key: keyof Omit<DispatchPlaceMetrics, 'dispatch_place_id'>; label: string }[] = [
  { key: 'por_recibir', label: 'Por recibir' },
  { key: 'en_punto', label: 'En punto' },
  { key: 'recolectadas', label: 'Recolectadas' },
  { key: 'entregadas', label: 'Entregadas' },
  { key: 'por_devolver', label: 'Por devolver' },
  { key: 'canceladas', label: 'Canceladas' },
];

// ── Sortable header ───────────────────────────────────────────────────────────

interface SortableHeaderProps {
  label: string;
  field: SortField;
  current_field: SortField;
  current_direction: 'asc' | 'desc';
  onSort: (field: SortField) => void;
}

function SortableHeader(props: SortableHeaderProps) {
  const is_active = props.current_field === props.field;
  return (
    <button
      type="button"
      className={twMerge(
        'flex items-center gap-1 hover:text-primary transition-colors',
        is_active && 'text-primary font-semibold',
      )}
      onClick={() => props.onSort(props.field)}
    >
      {props.label}
      {is_active
        ? <ArrowDownUp className="h-3.5 w-3.5" />
        : <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
      }
    </button>
  );
}

// ── Metrics cell ─────────────────────────────────────────────────────────────

interface MetricsCellProps {
  metrics: DispatchPlaceMetrics | undefined;
  is_loading: boolean;
}

function MetricsCell(props: MetricsCellProps) {
  if (props.is_loading) {
    return (
      <div className="flex items-center gap-3">
        {METRIC_GROUPS.map((group) => (
          <div key={group.key} className="skeleton h-8 w-10 rounded" />
        ))}
      </div>
    );
  }

  const metrics = props.metrics;

  return (
    <div className="flex items-center gap-4">
      {METRIC_GROUPS.map((group) => {
        const count = metrics ? metrics[group.key] : 0;
        const is_zero = count === 0;
        return (
          <div key={group.key} className="flex flex-col items-center min-w-[44px]">
            <span
              className={twMerge(
                'text-sm font-bold leading-none',
                is_zero ? 'text-base-content/30' : 'text-base-content',
              )}
            >
              {count}
            </span>
            <span className="mt-0.5 text-[9px] leading-none text-base-content/50 text-center whitespace-nowrap">
              {group.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * Puntos 99 list view.
 * Row click and three-dot menu open a detail drawer on the right.
 * The header includes a CSV download button that exports the full list
 * with per-place order metrics.
 */
export function DispatchPlacesPage() {
  const {
    items,
    total,
    displayed_count,
    isLoading,
    isFetching,
    is_metrics_fetching,
    isError,
    search_query,
    status_filter,
    sort_field,
    sort_direction,
    metrics_by_id,
    onSearchChange,
    onStatusFilterChange,
    onSortChange,
    onDownloadCsv,
  } = useDispatchPlacesPage();

  const [selected_place, setSelectedPlace] = useState<DispatchPlace | null>(null);

  const columns = useMemo<ColumnDef<DispatchPlace>[]>(
    () => [
      {
        accessorKey: 'name',
        header: () => (
          <SortableHeader
            label="Nombre"
            field="name"
            current_field={sort_field}
            current_direction={sort_direction}
            onSort={onSortChange}
          />
        ),
        cell: (info) => (
          <button
            type="button"
            className="font-bold text-left hover:text-primary transition-colors"
            onClick={() => setSelectedPlace(info.row.original)}
          >
            {info.getValue() as string}
          </button>
        ),
      },
      {
        id: 'city',
        header: () => (
          <SortableHeader
            label="Ciudad"
            field="city"
            current_field={sort_field}
            current_direction={sort_direction}
            onSort={onSortChange}
          />
        ),
        cell: (info) => info.row.original.address?.city ?? '—',
      },
      {
        accessorKey: 'country_code',
        header: 'País',
        size: 80,
        cell: (info) => (info.getValue() as string).toUpperCase(),
      },
      {
        accessorKey: 'type',
        header: 'Tipo',
        size: 100,
        cell: (info) => TYPE_LABEL[info.getValue() as DispatchPlaceType] ?? info.getValue(),
      },
      {
        accessorKey: 'status',
        header: () => (
          <SortableHeader
            label="Estado"
            field="status"
            current_field={sort_field}
            current_direction={sort_direction}
            onSort={onSortChange}
          />
        ),
        size: 120,
        cell: (info) => {
          const status = info.getValue() as DispatchPlaceStatus;
          return (
            <span className={twMerge('badge', STATUS_BADGE_CLASS[status] ?? 'badge-ghost')}>
              {STATUS_LABEL[status] ?? status}
            </span>
          );
        },
      },
      {
        accessorKey: 'capacity',
        header: () => (
          <SortableHeader
            label="Capacidad"
            field="capacity"
            current_field={sort_field}
            current_direction={sort_direction}
            onSort={onSortChange}
          />
        ),
        size: 110,
        meta: { align: 'center' },
        cell: (info) => formatNumber(info.getValue() as number),
      },
      {
        id: 'metrics',
        header: 'Órdenes',
        cell: (info) => (
          <MetricsCell
            metrics={metrics_by_id[info.row.original.id]}
            is_loading={is_metrics_fetching}
          />
        ),
      },
      {
        id: 'actions',
        size: 48,
        meta: { align: 'center' },
        header: '',
        cell: (info) => (
          <div className="dropdown dropdown-end">
            <button
              tabIndex={0}
              type="button"
              className="btn btn-ghost btn-xs btn-square"
              aria-label="Opciones"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            <ul
              tabIndex={0}
              className="dropdown-content z-[50] menu menu-sm bg-base-100 border border-base-300 rounded-box shadow-lg w-44 p-1"
            >
              <li>
                <button
                  type="button"
                  onClick={() => setSelectedPlace(info.row.original)}
                >
                  Detalle de punto99
                </button>
              </li>
              <li>
                <Link to={`/dispatch-places/${info.row.original.id}`}>
                  Ver página completa
                </Link>
              </li>
            </ul>
          </div>
        ),
      },
    ],
    [sort_field, sort_direction, onSortChange, metrics_by_id, is_metrics_fetching],
  );

  if (isError) {
    return (
      <div className="flex justify-center p-8">
        <div className="alert alert-error max-w-lg">
          <span>No se pudieron cargar los Puntos 99. Intenta de nuevo.</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* ── Header ── */}
        <header className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-3xl font-bold">Puntos 99</h1>
            <p className="text-base-content/70">
              {isLoading ? '…' : `${displayed_count} de ${total} puntos`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-sm gap-2"
              onClick={onDownloadCsv}
              disabled={isLoading || items.length === 0}
              title="Descargar lista completa como CSV"
            >
              <Download size={16} />
              CSV
            </button>
            <Link to="/dispatch-places/new" className="btn btn-primary btn-sm">
              <Plus size={18} />
              Añadir punto
            </Link>
          </div>
        </header>

        {/* ── Search + filters ── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-base-content/40" />
            <input
              type="text"
              className="input input-bordered w-full pl-9"
              placeholder="Buscar por nombre, ciudad, país…"
              value={search_query}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            {(['all', 'active', 'inactive', 'pending'] as const).map((status) => {
              const label: Record<string, string> = {
                all: 'Todos',
                active: 'Activos',
                inactive: 'Inactivos',
                pending: 'Pendientes',
              };
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => onStatusFilterChange(status)}
                  className={twMerge(
                    'btn btn-sm',
                    status_filter === status ? 'btn-primary' : 'btn-ghost',
                  )}
                >
                  {label[status]}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Table ── */}
        <DataTable columns={columns} data={items} isLoading={isLoading} isFetching={isFetching} />
      </div>

      {/* ── Detail drawer ── */}
      <DispatchPlaceDrawer
        place={selected_place}
        onClose={() => setSelectedPlace(null)}
      />
    </>
  );
}
